import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import {
  ShoppingCart, TrendingUp, TrendingDown, Calendar, Award,
  Send, Bot, RefreshCw, Package, Loader2, ChevronDown, ChevronUp,
  DollarSign, Truck, Star, AlertTriangle
} from 'lucide-react';

const PurchaseAgent = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchDashboard = async () => {
    try {
      const res = await api.get('/purchase-agent/dashboard');
      setDashboard(res.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMsg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatLoading(true);

    try {
      const res = await api.post('/purchase-agent/chat', { message: userMsg });
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      toast.error('Error en el chat');
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Error al procesar. Intenta de nuevo.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getUrgencyBadge = (urgency) => {
    switch (urgency) {
      case 'urgent': return <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">URGENTE</span>;
      case 'soon': return <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">PRONTO</span>;
      default: return <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">NORMAL</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      </div>
    );
  }

  const summary = dashboard?.summary || {};

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-teal-700 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Agente de Compras</h1>
                <p className="text-green-200 text-sm">Inteligencia para optimizar tus compras</p>
              </div>
            </div>
            <Button onClick={fetchDashboard} variant="outline" className="text-white border-white/30 hover:bg-white/10">
              <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
            </Button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Dashboard */}
          <div className="w-1/2 p-4 overflow-auto border-r">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3 mb-4">
              <div className="bg-white rounded-lg p-3 shadow-sm border">
                <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                  <Truck className="w-4 h-4" /> Proveedores
                </div>
                <div className="text-2xl font-bold">{summary.total_suppliers || 0}</div>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm border">
                <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                  <DollarSign className="w-4 h-4" /> Gastado (6m)
                </div>
                <div className="text-lg font-bold">${(summary.total_spent_6months || 0).toLocaleString()}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 shadow-sm border border-green-200">
                <div className="flex items-center gap-2 text-green-600 text-xs mb-1">
                  <TrendingDown className="w-4 h-4" /> Ahorro Potencial
                </div>
                <div className="text-lg font-bold text-green-600">${(summary.potential_savings || 0).toLocaleString()}</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 shadow-sm border border-orange-200">
                <div className="flex items-center gap-2 text-orange-600 text-xs mb-1">
                  <AlertTriangle className="w-4 h-4" /> Necesitan Stock
                </div>
                <div className="text-2xl font-bold text-orange-600">{summary.items_needing_stock || 0}</div>
              </div>
            </div>

            {/* Supplier Ranking */}
            <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-500" /> Ranking de Proveedores
              </h3>
              <div className="space-y-2">
                {dashboard?.supplier_ranking?.slice(0, 5).map((s, idx) => (
                  <div key={s.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-lg font-bold text-gray-400 shrink-0">#{idx + 1}</span>
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{s.name}</div>
                        <div className="text-xs text-gray-500">{s.purchase_count} compras · {s.items_variety} productos</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2 shrink-0">
                      <div className="text-right">
                        <div className="text-sm font-medium">${s.total_spent.toLocaleString()}</div>
                        <div className="text-xs text-gray-500">{s.most_active_day}</div>
                      </div>
                      <div className={`px-2 py-1 rounded text-sm font-bold ${getScoreColor(s.overall_score)}`}>
                        {s.overall_score}
                      </div>
                    </div>
                  </div>
                ))}
                {(!dashboard?.supplier_ranking || dashboard.supplier_ranking.length === 0) && (
                  <p className="text-gray-500 text-sm text-center py-4">Registra compras para ver ranking</p>
                )}
              </div>
            </div>

            {/* Purchase Calendar */}
            <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" /> Calendario Óptimo de Compras
              </h3>
              <div className="space-y-2">
                {dashboard?.purchase_calendar?.map((day) => (
                  <div key={day.day} className="border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedDay(expandedDay === day.day ? null : day.day)}
                      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-green-700">{day.day}</span>
                        <span className="text-sm text-gray-500">({day.suppliers.length} proveedores)</span>
                      </div>
                      {expandedDay === day.day ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    {expandedDay === day.day && (
                      <div className="p-3 space-y-2">
                        {day.suppliers.map((sup) => (
                          <div key={sup.name} className="flex justify-between items-start text-sm p-2 bg-green-50 rounded">
                            <div>
                              <div className="font-medium">{sup.name}</div>
                              <div className="text-xs text-gray-600">{sup.items.join(', ')}</div>
                            </div>
                            <div className="text-green-600 font-medium">
                              Ahorro: ${sup.potential_savings}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {(!dashboard?.purchase_calendar || dashboard.purchase_calendar.length === 0) && (
                  <p className="text-gray-500 text-sm text-center py-4">Registra más compras para generar recomendaciones</p>
                )}
              </div>
            </div>

            {/* Stock Needs */}
            {dashboard?.stock_needs?.length > 0 && (
              <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5 text-orange-500" /> Necesitan Reposición
                </h3>
                <div className="space-y-2">
                  {dashboard.stock_needs.slice(0, 5).map((item) => (
                    <div key={item.supply_id} className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{item.name || 'Sin nombre'}</div>
                        <div className="text-xs text-gray-600">
                          Stock: {item.current_stock} / Mín: {item.min_stock} {item.unit}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <span className="text-sm whitespace-nowrap">Comprar: <b>{item.suggested_qty} {item.unit}</b></span>
                        {getUrgencyBadge(item.urgency)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Best Items Analysis */}
            {dashboard?.item_analysis?.length > 0 && (
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-500" /> Oportunidades de Ahorro
                </h3>
                <div className="space-y-2">
                  {dashboard.item_analysis.filter(i => i.savings_potential > 0).slice(0, 5).map((item) => (
                    <div key={item.name} className="flex justify-between items-center p-2 bg-purple-50 rounded text-sm">
                      <div>
                        <div className="font-medium">{item.name}</div>
                        <div className="text-xs text-gray-600">
                          Mejor: {item.best_supplier} (${item.best_supplier_price}) · {item.best_day}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-purple-600 font-bold">Ahorro: ${item.savings_potential}</div>
                        <div className="text-xs text-gray-500">Promedio: ${item.avg_price}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Chat */}
          <div className="w-1/2 flex flex-col bg-white">
            <div className="p-3 border-b bg-gray-50 flex items-center gap-2">
              <Bot className="w-5 h-5 text-green-600" />
              <span className="font-medium">Chat con el Agente de Compras</span>
            </div>

            <div className="flex-1 overflow-auto p-4 space-y-4">
              {chatMessages.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-green-300" />
                  <p className="font-medium">¡Hola! Soy tu Agente de Compras</p>
                  <p className="text-sm mt-1">Pregúntame sobre proveedores, precios o cuándo comprar</p>
                  <div className="mt-4 space-y-2">
                    <button 
                      onClick={() => setChatInput('¿Quién me da mejor precio en camarón?')}
                      className="block w-full text-left px-3 py-2 bg-green-50 hover:bg-green-100 rounded text-sm text-green-700"
                    >
                      💡 ¿Quién me da mejor precio en camarón?
                    </button>
                    <button 
                      onClick={() => setChatInput('¿Qué día es mejor para comprar mariscos?')}
                      className="block w-full text-left px-3 py-2 bg-green-50 hover:bg-green-100 rounded text-sm text-green-700"
                    >
                      💡 ¿Qué día es mejor para comprar mariscos?
                    </button>
                    <button 
                      onClick={() => setChatInput('¿Cómo puedo reducir mis costos de compras?')}
                      className="block w-full text-left px-3 py-2 bg-green-50 hover:bg-green-100 rounded text-sm text-green-700"
                    >
                      💡 ¿Cómo puedo reducir mis costos de compras?
                    </button>
                  </div>
                </div>
              )}
              {chatMessages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] p-3 rounded-lg ${
                    msg.role === 'user' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 p-3 rounded-lg flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm text-gray-600">Analizando...</span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div className="p-3 border-t">
              <div className="flex gap-2">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Pregunta sobre proveedores, precios..."
                  disabled={chatLoading}
                  className="flex-1"
                />
                <Button onClick={sendMessage} disabled={chatLoading || !chatInput.trim()} className="bg-green-600 hover:bg-green-700">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseAgent;
